<template>
  <Teleport to="body">
    <dialog ref="dialog" class="family-story" aria-labelledby="family-story-title" @close="stop" @click="closeOnBackdrop">
      <div class="family-story__header">
        <h2 id="family-story-title">{{ current.title === 'Family Story' ? 'The Family Story' : current.title }}</h2>
        <button class="family-story__close" type="button" aria-label="Close video" autofocus @click="dialog.close()">×</button>
      </div>
      <video v-show="!bonusOpen && !saved" ref="video" class="family-story__video" :src="current.url" controls playsinline preload="none"
        @ended="finishVideo" @play="ended = false" @error="videoFailed = true">
        Your browser does not support video playback.
      </video>
      <p v-if="videoFailed" class="family-story__error" role="alert">
        The video couldn't load. Check your connection and <button type="button" @click="retryVideo">try again</button>.
      </p>
      <div v-if="ended" class="family-story__end" aria-live="polite">
        <template v-if="!bonusOpen && !saved">
          <template v-if="catalogReady">
            <p>{{ completed.size }} of {{ videos.length }} stories watched</p>
            <button v-if="!allWatched" ref="endAction" class="family-story__button" type="button" @click="watchMore">Watch More</button>
            <button v-else ref="endAction" class="family-story__button" type="button" @click="openBonus">FAN BONUS!!!</button>
            <p v-if="!allWatched" class="family-story__next">Up next: {{ nextVideo.title }}</p>
          </template>
          <template v-else>
            <p>{{ catalogError || 'Loading the next story…' }}</p>
            <button v-if="catalogError" class="family-story__button" type="button" @click="loadCatalog">Try Again</button>
          </template>
        </template>
        <form v-if="bonusOpen && !saved" class="family-story__form" @submit.prevent="submitBonus">
          <h3>FAN BONUS!!!</h3>
          <p>You watched the whole collection! Leave your details, then confirm your email so our team can follow up about a special gift.</p>
          <label>Email address<input ref="emailInput" v-model="email" type="email" autocomplete="email" maxlength="254" required :disabled="saving" /></label>
          <label>Phone number<input v-model="phone" type="tel" autocomplete="tel" maxlength="40" required :disabled="saving" /></label>
          <label class="family-story__consent"><input v-model="consent" type="checkbox" required :disabled="saving" />{{ consentText }}</label>
          <p class="family-story__next">Your details are for gift follow-up. This does not subscribe you to marketing.</p>
          <p v-if="formError" role="alert">{{ formError }}</p>
          <button class="family-story__button" type="submit" :disabled="saving">{{ saving ? 'Saving…' : 'Claim My Gift' }}</button>
        </form>
        <div v-if="saved" ref="successMessage" tabindex="-1" role="status">
          <h3>{{ savedMode === 'capture' ? 'Verification email captured' : 'One more step: check your email' }}</h3>
          <p>{{ savedMessage }}</p>
        </div>
      </div>
    </dialog>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { apiClient } from '@/services/api'

const emit = defineEmits(['restore-focus'])
const dialog = ref(null)
const video = ref(null)
const endAction = ref(null)
const emailInput = ref(null)
const successMessage = ref(null)
const videos = ref([{ id: 'family-story', title: 'Family Story', url: 'https://storage.googleapis.com/banjinos-marketing-site-assets/movies/FamilyStory8.0.mp4' }])
const currentIndex = ref(0)
const current = computed(() => videos.value[currentIndex.value])
const completed = ref(new Set())
const ended = ref(false)
const videoFailed = ref(false)
const catalogReady = ref(false)
const catalogError = ref('')
const campaign = ref('')
const consentText = ref('')
const bonusOpen = ref(false)
const saved = ref(false)
const savedMessage = ref('')
const savedMode = ref('')
const saving = ref(false)
const email = ref('')
const phone = ref('')
const consent = ref(false)
const formError = ref('')
const allWatched = computed(() => catalogReady.value && videos.value.every(item => completed.value.has(item.id)))
const nextIndex = computed(() => {
  for (let step = 1; step <= videos.value.length; step++) {
    const index = (currentIndex.value + step) % videos.value.length
    if (!completed.value.has(videos.value[index].id)) return index
  }
  return currentIndex.value
})
const nextVideo = computed(() => videos.value[nextIndex.value])

async function loadCatalog () {
  catalogError.value = ''
  try {
    const { data: { data } } = await apiClient.get('/v1/fan-bonus/videos')
    const index = data.videos.findIndex(item => item.id === current.value.id)
    if (index < 0) throw new Error('Missing story')
    videos.value = data.videos
    currentIndex.value = index
    campaign.value = data.campaign
    consentText.value = data.consent_text
    catalogReady.value = true
  } catch {
    catalogError.value = 'The next stories could not load. Please try again.'
  }
}
onMounted(loadCatalog)

function show () {
  dialog.value.showModal()
  if (ended.value) return // Keep the end card and progress when reopening.
  if (videoFailed.value) retryVideo()
  else video.value.play().catch(() => {})
}
function stop () {
  video.value.pause()
  emit('restore-focus')
}
function retryVideo () {
  videoFailed.value = false
  video.value.load()
  video.value.play().catch(() => {})
}
async function finishVideo () {
  completed.value.add(current.value.id)
  ended.value = true
  await nextTick()
  endAction.value?.focus()
  endAction.value?.scrollIntoView({ block: 'nearest' })
}
async function watchMore () {
  if (!catalogReady.value || allWatched.value) return
  currentIndex.value = nextIndex.value
  ended.value = false
  videoFailed.value = false
  await nextTick()
  video.value.load()
  video.value.play().catch(() => {})
  video.value.focus()
  video.value.scrollIntoView({ block: 'nearest' })
}
async function openBonus () {
  if (!allWatched.value) return
  bonusOpen.value = true
  await nextTick()
  emailInput.value?.focus()
}
async function submitBonus () {
  if (saving.value || !allWatched.value) return
  formError.value = ''
  saving.value = true
  try {
    const response = await apiClient.post('/v1/fan-bonus', {
      campaign: campaign.value, email: email.value, phone: phone.value,
      contact_consent: consent.value, completed_video_ids: [...completed.value]
    })
    if (!response.data.success) throw new Error('Request not saved')
    savedMessage.value = response.data.data?.message || 'Check your inbox to confirm your email.'
    savedMode.value = response.data.data?.delivery_mode || ''
    saved.value = true
    email.value = ''
    phone.value = ''
    await nextTick()
    successMessage.value?.focus()
  } catch (error) {
    formError.value = [400, 429, 503].includes(error.response?.status)
      ? (error.response.data?.error || 'Email delivery is temporarily unavailable. Please try again shortly.') : 'We could not save your request. Please try again. Your details are still here.'
  } finally { saving.value = false }
}
function closeOnBackdrop (event) {
  if (event.target !== dialog.value) return
  const bounds = dialog.value.getBoundingClientRect()
  if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.value.close()
}
defineExpose({ show })
</script>

<style scoped>
.family-story {
  width: min(960px, calc(100vw - 32px));
  max-width: none;
  max-height: calc(100dvh - 32px);
  margin: auto;
  padding: 0;
  overflow: auto;
  border: 0;
  border-radius: 12px;
  background: #101b1d;
  color: #fff5e6;
  box-shadow: 0 20px 80px rgb(0 0 0 / 40%);
}
.family-story::backdrop { background: rgb(0 0 0 / 78%); }
.family-story__header { position: sticky; top: 0; z-index: 1; background: #101b1d; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 12px 20px; }
.family-story__header h2 { margin: 0; font: 600 20px/1.3 'Helvetica Neue', Arial, sans-serif; }
.family-story .family-story__close {
  flex: none;
  width: 44px;
  height: 44px;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent !important;
  color: #fff5e6;
  box-shadow: none !important;
  font: 32px/1 Arial, sans-serif !important;
  cursor: pointer;
}
.family-story__close:focus-visible { outline: 2px solid #fff5e6; outline-offset: 2px; }
.family-story__video { display: block; width: 100%; max-height: calc(100dvh - 112px); background: #000; }
.family-story__error { margin: 0; padding: 16px 20px; }
.family-story__error a { color: inherit; text-decoration: underline; }


.family-story__end { padding: 24px; text-align: center; font: 16px/1.5 'Helvetica Neue', Arial, sans-serif; }
.family-story p, .family-story label { color: #fff5e6 !important; font: 400 16px/1.5 'Helvetica Neue', Arial, sans-serif !important; }
.family-story__end p { margin: 0 0 16px; }
.family-story__end h3 { margin: 0 0 16px; color: #fff5e6; font: 700 24px/1.3 'Helvetica Neue', Arial, sans-serif; }
.family-story .family-story__button { background: #fff5e6 !important; color: #24513b !important; border: 0; border-radius: 30px; padding: 14px 28px; font: 700 16px/1.3 'Helvetica Neue', Arial, sans-serif !important; cursor: pointer; }
.family-story__button:disabled { opacity: .6; cursor: wait; }
.family-story__next { font-size: 14px; opacity: .8; margin-top: 12px !important; }
.family-story__form { max-width: 460px; margin: auto; text-align: left; }
.family-story__form label { display: block; margin: 16px 0; }
.family-story__form input:not([type=checkbox]) { display: block; width: 100%; box-sizing: border-box; border: 1px solid #c9c2b4; border-radius: 6px; background: #fff; color: #18231e; padding: 12px; margin-top: 6px; font: inherit; }
.family-story__form .family-story__consent { display: flex; align-items: flex-start; gap: 12px; }
.family-story__consent input { flex: none; width: 20px; height: 20px; margin-top: 2px; accent-color: #d79c44; }
.family-story__form input:focus-visible, .family-story__button:focus-visible { outline: 3px solid #e6af5d; outline-offset: 3px; }
.family-story__error button { color: #fff5e6; text-decoration: underline; background: transparent !important; }
</style>
